import Link from 'next/link'
import { Fragment } from 'react/jsx-runtime'
import { NavigationLink, Settings } from '@/src/@types/statamic'
import Container from '@/src/components/layout/Container'

export default function Footer({
  footerNavigation,
  settings,
}: {
  footerNavigation: NavigationLink[]
  settings: Settings['settings']
}) {
  return (
    <footer className="relative overflow-hidden bg-gray-950 py-12 text-white lg:py-16">
      <Container>
        <Link href="/" className="block">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="171"
            height="21"
            fill="none"
            className="drop-shadow-xs"
          >
            <path
              fill="#d97706"
              d="M66 7a4 4 0 0 1 4-4h8a4 4 0 0 1 2 .536V0h6v3h6a4 4 0 0 1 3 1.355A4 4 0 0 1 98 3h8a4 4 0 0 1 3 1.355A4 4 0 0 1 112 3h8a4 4 0 0 1 3 1.355A4 4 0 0 1 126 3h8a4 4 0 0 1 4 4v6h-7v8h-20a3 3 0 0 1-3-3v-5h-5v8H61a4 4 0 0 1-4-4v-4h-5v8H18a3 3 0 0 1-3-3v-5h-4v8H1v-8H0V7h1a4 4 0 0 1 4-4h8.5c1.066 0 2.033.417 2.75 1.097A3.99 3.99 0 0 1 19 3h8a4 4 0 0 1 2 .536V0h6v3h6a4 4 0 0 1 3 1.355A4 4 0 0 1 47 3h8a4 4 0 0 1 2 .536V0h9zm95-4h6a4 4 0 0 1 4 4v14h-26a4 4 0 0 1-3-1.356V21h-8v-6h7V7a4 4 0 0 1 4-4h9c.351 0 .687.063 1 .174V0h6z"
            />
            <path
              fill="#fff"
              d="M13.5 5a2 2 0 0 1 2 2H9v2h4v2H9v8H3v-8H2V9h1V7a2 2 0 0 1 2-2z"
            />
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M27 5a2 2 0 0 1 2 2v12H18a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h9V7H17a2 2 0 0 1 2-2zm-5 12h2v-5h-2z"
              clipRule="evenodd"
            />
            <path
              fill="#fff"
              d="M33 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2zM55 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2zM64 17h2v2h-5a2 2 0 0 1-2-2V2h5z"
            />
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M78 5a2 2 0 0 1 2 2v4h-7v6h2v-4h5v5a1 1 0 0 1-1 1H69a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2zm-5 4h2V7h-2z"
              clipRule="evenodd"
            />
            <path
              fill="#fff"
              d="M84 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2zM106 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2z"
            />
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M120 5a2 2 0 0 1 2 2v4h-7v6h2v-4h5v5a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2zm-5 4h2V7h-2z"
              clipRule="evenodd"
            />
            <path
              fill="#fff"
              d="M134 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2zM140 17v2h-4v-2zM154 5a1 1 0 0 1 1 1v5h-5V7h-2v10h2v-4h5v5a1 1 0 0 1-1 1h-9a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM159 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2z"
            />
          </svg>
          <span className="sr-only">Startseite</span>
        </Link>
        <div className="mt-8 flex flex-col gap-8 lg:grid lg:grid-cols-4 lg:gap-5">
          {settings.address && (
            <div>
              <p className="font-bold">Kontakt</p>
              <address
                className="mt-1 space-y-1 not-italic"
                dangerouslySetInnerHTML={{ __html: settings.address }}
              />
            </div>
          )}
          {footerNavigation.map((section) => (
            <div key={section.page.title}>
              <p className="font-bold">{section.page.title}</p>
              <ul className="mt-4 space-y-4">
                {section.children.map((link) => (
                  <Fragment key={link.page.title}>
                    {link.page.url && (
                      <li key={link.page.title}>
                        <Link href={link.page.url} className="text-sm">
                          {link.page.title}
                        </Link>
                      </li>
                    )}
                  </Fragment>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 text-sm text-gray-300">
          <p className="max-w-120 italic">
            Fahrlehrer.ch – seit 25 Jahren das Fahrschulverzeichnis für die
            Schweiz. Fahrlehrer:innen finden und vergleichen, mit vielen Infos
            über Preise, Ausweiskategorien und Führerausweise.
          </p>
          <p className="mt-5">
            @ Copyright {new Date().getFullYear()} Fahrlehrer.ch - eine Marke
            der{' '}
            <Link href="https://blinkdrive.ch" target="_blank">
              BLINK Fahrschule
            </Link>
          </p>
        </div>
      </Container>
    </footer>
  )
}
